import {
  GetStaticPathsContext,
  GetStaticPropsContext,
  InferGetStaticPropsType,
} from 'next';
import { getAllMenuPage } from 'repos/menu';
import { getAllUseCases } from 'repos/solutionPage';

import ContactCTABlock from 'components/ContactCTABlock';
import CustomersBlock from 'components/CustomersBlock';
import FAQBlock from 'components/FAQBlock';
import HighlightsBlock from 'components/HighlightsBlock';
import TestimonialsBlock from 'components/TestimonialsBlock';
import FeaturesBlock from 'components/UseCasePage/FeatureBlocks';
import Hero from 'components/UseCasePage/Hero';
import WhysBlock from 'components/WhysBlock';
import Layout from 'components/layout';
import SEO from 'components/seo';

export default function UseCase({
  menuParams,
  useCase,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const {
    title,
    heroTitle,
    heroSubtitle,
    heroButton1Link,
    heroButton1Text,
    heroButton2Link,
    heroButton2Text,
    heroImage,
    customerTitle,
    customers,
    whyTitle,
    whySubtitle,
    whys,
    highlights,
    testimonialTitle,
    testimonials,
    featureTitle,
    featureSubtitle,
    features,
    faqTitle,
    faqs: questions,
    ctaBookDemoTitle,
    ctaContactUsTitle,
    ctaTitle,
  } = useCase.fields;

  return (
    <Layout menuParams={menuParams}>
      <SEO title={title} />
      <Hero
        topTitle={title}
        title={heroTitle}
        subtitle={heroSubtitle}
        button1Text={heroButton1Text}
        button1Link={heroButton1Link}
        button2Text={heroButton2Text}
        button2Link={heroButton2Link}
        image={heroImage}
      />

      <CustomersBlock title={customerTitle} customers={customers} />
      <WhysBlock title={whyTitle} subtitle={whySubtitle} whys={whys} />
      <HighlightsBlock highlights={highlights} />
      <TestimonialsBlock title={testimonialTitle} testimonials={testimonials} />
      <FeaturesBlock
        title={featureTitle}
        subtitle={featureSubtitle}
        features={features}
      />
      <FAQBlock title={faqTitle} questions={questions} />
      <ContactCTABlock
        demoButtonText={ctaBookDemoTitle}
        text={ctaTitle}
        buttonText={ctaContactUsTitle}
      />
    </Layout>
  );
}

export async function getStaticPaths({ locales }: GetStaticPathsContext) {
  const useCases = (await getAllUseCases({})).filter(
    (u) => !u.fields.isIndustry
  );
  const paths = (locales || []).flatMap((locale) => {
    return useCases.map((useCase) => ({
      locale,
      params: {
        useCase: useCase.fields.slug,
      },
    }));
  });

  return {
    paths,
    fallback: false,
  };
}

export const getStaticProps = async ({
  locale,
  params,
}: GetStaticPropsContext) => {
  const slug = params?.useCase;
  if (!slug) {
    throw new Error('No useCase provided');
  }

  const useCases = (await getAllUseCases({ locale })).filter(
    (u) => !u.fields.isIndustry
  );
  const useCase = useCases.find((uc) => uc.fields.slug === slug);

  if (!useCase) {
    throw new Error('No useCase found');
  }
  const menuParams = await getAllMenuPage({ locale });

  return {
    props: {
      useCase,
      menuParams,
    },
  };
};
